import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, RefreshCcw, Search, PackageCheck, PackageMinus, TrendingUp } from "lucide-react";
import { formatINR } from "@/lib/permissions";

// Default range = current Indian FY (Apr 1 → today)
function defaultRange() {
  const today = new Date();
  const y = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return {
    start: `${y}-04-01`,
    end: today.toISOString().slice(0, 10),
  };
}

const money = (n) => formatINR(n || 0);
const qty = (n) => (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export default function ConsumableLedger() {
  const navigate = useNavigate();
  const [range, setRange] = useState(defaultRange());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/consumables/ledger", {
        params: { start_date: range.start, end_date: range.end },
      });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load ledger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) =>
      `${r.code || ""} ${r.name || ""} ${r.category || ""}`.toLowerCase().includes(query),
    );
  }, [rows, q]);

  const totals = useMemo(() => {
    const t = { opening_value: 0, inward_value: 0, outward_value: 0, closing_value: 0 };
    filtered.forEach((r) => {
      t.opening_value += Number(r.opening_value) || 0;
      t.inward_value += Number(r.inward_value) || 0;
      t.outward_value += Number(r.outward_value) || 0;
      t.closing_value += Number(r.closing_value) || 0;
    });
    return t;
  }, [filtered]);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(
        `${API_BASE}/consumables/ledger.xlsx?start_date=${range.start}&end_date=${range.end}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consumables_ledger_${range.start}_to_${range.end}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export ledger");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="consumable-ledger-page">
      <button
        onClick={() => navigate("/consumables")}
        className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Consumables
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Stores · Ledger</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Issue / Receive Ledger</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Opening + inward (receipts) − outward (issues) = closing. Values use the unit cost captured at each transaction.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={downloadExcel}
          disabled={downloading}
          data-testid="ledger-export-xlsx-btn"
        >
          <Download className="w-4 h-4 mr-2" /> {downloading ? "Preparing..." : "Export Excel"}
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">Start Date</Label>
            <Input
              type="date"
              value={range.start}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="w-44"
              data-testid="ledger-start-date"
            />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">End Date</Label>
            <Input
              type="date"
              value={range.end}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="w-44"
              data-testid="ledger-end-date"
            />
          </div>
          <Button
            onClick={load}
            disabled={loading}
            className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
            data-testid="ledger-apply-btn"
          >
            <RefreshCcw className="w-4 h-4 mr-2" /> {loading ? "Loading..." : "Refresh"}
          </Button>
          <div className="relative flex-1 min-w-[200px] ml-auto">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by code, name, category..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="ledger-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi icon={<PackageCheck className="w-4 h-4" />} label="Opening Value" value={money(totals.opening_value)} tint="slate" />
        <Kpi icon={<TrendingUp className="w-4 h-4" />} label="Inward Value" value={money(totals.inward_value)} tint="emerald" />
        <Kpi icon={<PackageMinus className="w-4 h-4" />} label="Outward Value" value={money(totals.outward_value)} tint="amber" />
        <Kpi icon={<PackageCheck className="w-4 h-4" />} label="Closing Value" value={money(totals.closing_value)} tint="rose" />
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-slate-500 font-semibold text-[11px] uppercase tracking-wider">
                <th className="px-3 py-3 text-left">Code / Name</th>
                <th className="px-3 py-3 text-left">Category</th>
                <th className="px-3 py-3 text-right border-l border-slate-200" colSpan={2}>Opening</th>
                <th className="px-3 py-3 text-right border-l border-emerald-200 bg-emerald-50" colSpan={2}>Inward</th>
                <th className="px-3 py-3 text-right border-l border-amber-200 bg-amber-50" colSpan={2}>Outward</th>
                <th className="px-3 py-3 text-right border-l border-rose-200 bg-rose-50" colSpan={2}>Closing</th>
              </tr>
              <tr className="text-slate-400 text-[10px] uppercase border-b border-slate-100">
                <th className="px-3 py-2"></th>
                <th className="px-3 py-2"></th>
                <th className="px-3 py-2 text-right border-l border-slate-200">Qty</th>
                <th className="px-3 py-2 text-right">Value</th>
                <th className="px-3 py-2 text-right border-l border-emerald-200 bg-emerald-50/50">Qty</th>
                <th className="px-3 py-2 text-right bg-emerald-50/50">Value</th>
                <th className="px-3 py-2 text-right border-l border-amber-200 bg-amber-50/50">Qty</th>
                <th className="px-3 py-2 text-right bg-amber-50/50">Value</th>
                <th className="px-3 py-2 text-right border-l border-rose-200 bg-rose-50/50">Qty</th>
                <th className="px-3 py-2 text-right bg-rose-50/50">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400">Loading ledger…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-16 text-center text-slate-500">No consumables match the current filter.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`ledger-row-${r.code}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-mono text-xs font-semibold text-[#7C1D3A]">{r.code}</div>
                    <div className="text-slate-900 font-medium">{r.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {qty(r.current_stock)} {r.unit_of_measure} in stock @ {money(r.unit_cost || 0)}/{r.unit_of_measure}
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10px]">{r.category || "—"}</Badge></td>
                  <td className="px-3 py-2.5 text-right font-mono border-l border-slate-200">{qty(r.opening_qty)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-700">{money(r.opening_value)}</td>
                  <td className="px-3 py-2.5 text-right font-mono border-l border-emerald-200 bg-emerald-50/40 text-emerald-800">{qty(r.inward_qty)}</td>
                  <td className="px-3 py-2.5 text-right font-mono bg-emerald-50/40 text-emerald-800">{money(r.inward_value)}</td>
                  <td className="px-3 py-2.5 text-right font-mono border-l border-amber-200 bg-amber-50/40 text-amber-800">{qty(r.outward_qty)}</td>
                  <td className="px-3 py-2.5 text-right font-mono bg-amber-50/40 text-amber-800">{money(r.outward_value)}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold border-l border-rose-200 bg-rose-50/40 text-rose-800">{qty(r.closing_qty)}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold bg-rose-50/40 text-rose-800">{money(r.closing_value)}</td>
                </tr>
              ))}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="border-t-2 border-slate-300 bg-slate-100">
                <tr className="font-semibold text-slate-900 text-sm">
                  <td className="px-3 py-3" colSpan={2}>TOTALS (filtered)</td>
                  <td className="px-3 py-3 text-right border-l border-slate-200">—</td>
                  <td className="px-3 py-3 text-right font-mono">{money(totals.opening_value)}</td>
                  <td className="px-3 py-3 text-right border-l border-emerald-200">—</td>
                  <td className="px-3 py-3 text-right font-mono text-emerald-800">{money(totals.inward_value)}</td>
                  <td className="px-3 py-3 text-right border-l border-amber-200">—</td>
                  <td className="px-3 py-3 text-right font-mono text-amber-800">{money(totals.outward_value)}</td>
                  <td className="px-3 py-3 text-right border-l border-rose-200">—</td>
                  <td className="px-3 py-3 text-right font-mono text-rose-800">{money(totals.closing_value)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}

function Kpi({ icon, label, value, tint = "slate" }) {
  const tints = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <Card className={`${tints[tint]} border-0`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[11px] uppercase font-semibold tracking-wider opacity-80">
          {icon}
          {label}
        </div>
        <div className="mt-1.5 font-mono text-xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}
